import { Module, Mutation, VuexModule, getModule as getStoreModule } from 'vuex-module-decorators';
import { Action } from '@libraries/vuex-module-decorators/Action';
import UserService from '@app/services/auth/UserService';
import Sentry from '@madebyheyday/sentry-util';

import StateCacheService from '@/app/src/services/cache/StateCacheService';
import {
	throwCacheErrorIfUnsupportedInClient,
	throwIfNotCachingUnsupportedInClient
} from '@app/types/errors/CacheError';
import OfflineError, { isNetworkOrOfflineErrorAndAppIsOffline } from '@app/types/errors/OfflineError';
import AppStoreModule from '@app/store/modules/AppStoreModule';
import store from '../store';
import IUser from '../../types/base/auth/IUser';

// import Role from '../../types/base/auth/Role';

const appStore: AppStoreModule = getStoreModule(AppStoreModule);

const moduleId = 'user';

@Module({
	name: moduleId,
	store,
	dynamic: true,
	namespaced: true
})
export default class UserStoreModule extends VuexModule {
	/**
	 * Currently logged in management user
	 */
	public currentUser: IUser | null = null;

	@Action
	public async saveState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const userStateCacheService: StateCacheService = new StateCacheService('user');
		await userStateCacheService.save('currentUser', this.currentUser);
	}

	@Action
	public async restoreState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const userStateCacheService: StateCacheService = new StateCacheService('user');

		if ((await userStateCacheService.keys()).length > 0) {
			this.setCurrentUser((await userStateCacheService.restore('currentUser')) || this.currentUser);
			return;
		}

		// Legacy support for all store modules stored in a single table

		const cache: StateCacheService = new StateCacheService();

		const cachedState: Record<string, any> | null = await cache.restore(moduleId);
		if (!cachedState) return;

		if (cachedState.currentUser) this.setCurrentUser(cachedState.currentUser);
	}

	@Action
	public async saveStateIfSupported(): Promise<void> {
		return this.saveState().catch(throwIfNotCachingUnsupportedInClient);
	}

	@Action
	public async restoreStateIfSupported(): Promise<void> {
		return this.restoreState().catch(throwIfNotCachingUnsupportedInClient);
	}

	/**
	 * Log in a user via a management token
	 */
	@Action
	public async loadCurrentUser(): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();

			const userService: UserService = new UserService();
			const currentUser: IUser = await userService.getCurrentUser();

			this.setCurrentUser(currentUser);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load current user as application is offline');
			}
		}
	}

	/**
	 * Log out the current user
	 */
	@Action
	public resetCurrentUser(): void {
		this.setCurrentUser(null);
	}

	@Mutation
	protected setCurrentUser(user: IUser | null): void {
		this.currentUser = user;
		/*
		this.currentUser = {
			firstName: 'Ann',
			lastName: 'Seibert',
			avatarUrl: 'https://avatars0.githubusercontent.com/u/33252140?v=4',
			email: 'david@seibert.io',
			activated: true,
			signInCount: 166,
			confirmed: true,
			sys: {
				type: 'User',
				id: '7ulM7Gs7OmWMhjvpzrLaFO',
				version: 167,
				createdAt: '2018-08-07T05:22:02Z',
				updatedAt: '2020-02-11T13:00:08Z'
			},
			role: Role.SalesManager
		};
		*/
		if (user) {
			Sentry.setUser({
				id: user.sys.id,
				email: user.email,
				role: user.role,
				firstName: user.firstName,
				lastName: user.lastName
			});
		}
	}

	public get loggedIn(): boolean {
		return !!this.currentUser;
	}
}
